import { Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PRISM_PHONE, PRISM_PHONE_HREF } from "./prismData";

/**
 * Section E — "For referring providers".
 * Placed directly before the closing CTA / "Start with a conversation".
 * Visually distinct navy band; anchor id="providers" so it links as /prism#providers.
 *
 * GUARDRAIL: only FDA claim is FDA-cleared as an adjunct for PTSD symptoms.
 * The depression protocol is explicitly noted as not FDA-cleared for depression.
 */
const mayBenefit = [
  "Chronic PTSD with persistent intrusion, avoidance, mood or hyperarousal symptoms despite therapy and/or medication",
  "Patients who can't tolerate or don't want to add another medication",
  "Patients who stall in or avoid trauma-focused therapy (Prism requires no trauma retelling)",
  "Depression marked by anhedonia (flatness, low motivation, \u201Cfunctional but disconnected\u201D) for our Prism depression protocol (not FDA-cleared for depression; used adjunctively)",
];

export default function ProvidersSection() {
  return (
    <section
      id="providers"
      className="relative overflow-hidden bg-gradient-navy py-16 text-primary-foreground lg:py-24"
      aria-labelledby="prism-providers-heading"
    >
      <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
      <div className="container-px relative mx-auto max-w-3xl">
        <span className="text-sm font-semibold uppercase tracking-widest text-accent">
          For clinicians
        </span>
        <h2
          id="prism-providers-heading"
          className="mt-3 font-headings text-2xl font-bold sm:text-3xl lg:text-[2.5rem]"
        >
          A non-medication option to add to your patient&rsquo;s care
        </h2>
        <p className="mt-6 text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
          Neuro Wellness TMS Centers of America is an authorized Prism provider
          in Coral Springs. Prism is a prescription EEG neurofeedback device,
          FDA-cleared as an adjunct for symptoms associated with PTSD. It adds
          to the care your patient already receives: they stay with their
          therapist, and medication decisions stay with their prescriber.
        </p>

        <h3 className="mt-8 font-headings text-lg font-semibold">
          Patients who may benefit
        </h3>
        <ul className="mt-4 space-y-2.5">
          {mayBenefit.map((item) => (
            <li
              key={item}
              className="flex gap-2.5 text-[15px] leading-relaxed text-primary-foreground/80"
            >
              <span
                aria-hidden="true"
                className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"
              />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h3 className="mt-8 font-headings text-lg font-semibold">
          What to expect
        </h3>
        <p className="mt-3 text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
          15 sessions of about 45 minutes, typically twice weekly over about 8
          weeks. In a multi-center study, 67% of patients showed clinically
          significant improvement on CAPS-5 and 90% completed the course
          (Fruchter E, et al. Psychiatry Research. 2024;333:115711). We send
          progress updates and coordinate shared care.
        </p>

        <h3 className="mt-8 font-headings text-lg font-semibold">
          How to refer
        </h3>
        <p className="mt-3 text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
          Use our{" "}
          <a
            href="https://neurowellnesstms.com/patient-eligibility-checklist"
            className="font-semibold text-accent underline underline-offset-4"
          >
            Physician Referral Checklist
          </a>{" "}
          or call{" "}
          <a
            href={PRISM_PHONE_HREF}
            className="font-semibold text-accent underline underline-offset-4"
          >
            {PRISM_PHONE}
          </a>
          . We&rsquo;re also glad to schedule a lunch-and-learn for your
          practice on recognizing PTSD clusters and anhedonic depression in
          high-functioning patients.
        </p>

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <Button
            asChild
            size="lg"
            className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
          >
            <a href="https://neurowellnesstms.com/patient-eligibility-checklist">
              Refer a patient
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
          >
            <a href="https://neurowellnesstms.com/contactus">
              <Phone className="mr-2 h-4 w-4" />
              Book a lunch-and-learn
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
